import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Plug,
  FileCheck,
  MousePointerClick,
  Rocket,
  Eye,
  Target,
  Search,
  TrendingDown,
  DollarSign
} from 'lucide-react';
import { PageLayout } from '@/components';
import DemoRequestModal from './DemoRequestModal';

const FreeTrial: React.FC = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'demo' | 'trial'>('demo');

  const openModal = (type: 'demo' | 'trial') => {
    setModalType(type);
    setModalOpen(true);
  };

  const features = [
    { icon: Plug, text: t('freeTrial.featureIntegration') },
    { icon: FileCheck, text: t('freeTrial.featureNoDuplication') },
    { icon: MousePointerClick, text: t('freeTrial.featureNavigate') },
  ];

  const keyFeatures = [
    { icon: Rocket, text: t('freeTrial.featureIdentifyDocs') },
    { icon: Eye, text: t('freeTrial.featureVisibility') },
    { icon: Target, text: t('freeTrial.featureCorrectArch') },
    { icon: Search, text: t('freeTrial.featureIdentifyGaps') },
  ];

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="relative pt-20 pb-20 overflow-hidden sm:pt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"></div>

        <div className="relative container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium mb-6">
              {t('freeTrial.badge')}
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-tight text-white">
              {t('freeTrial.heroTitle')}
              <br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                {t('freeTrial.heroTitleHighlight')}
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
              {t('freeTrial.heroSubtitle')}
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              {features.map((feature, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-slate-200"
                >
                  <feature.icon size={18} className="text-blue-400" />
                  <span className="text-sm font-medium">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => openModal('trial')}
                className="group px-8 py-4 bg-white text-slate-900 rounded-xl font-semibold text-lg transition-all hover:bg-slate-100 flex items-center gap-3 transform hover:scale-[1.02] active:scale-95"
              >
                {t('freeTrial.startYourFreeTrial')}
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => openModal('demo')}
                className="px-8 py-4 bg-transparent hover:bg-white/10 border border-slate-400 rounded-xl font-medium text-lg transition-all text-white"
              >
                {t('freeTrial.requestADemo')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('freeTrial.accelerateTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('freeTrial.accelerateSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {keyFeatures.map((feature, i) => (
              <div
                key={i}
                className="group flex items-start gap-4 p-6 bg-slate-50 border border-slate-200 rounded-2xl hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <feature.icon size={24} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-medium text-slate-900">{feature.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Story Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('freeTrial.customerSuccessTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('freeTrial.customerSuccessSubtitle')}
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12 shadow-lg">
            <blockquote className="text-xl md:text-2xl text-slate-700 leading-relaxed mb-8 text-center italic">
              "{t('freeTrial.customerQuote')}"
            </blockquote>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="group p-6 bg-slate-50 border border-slate-200 rounded-2xl text-center hover:border-blue-300 hover:shadow-lg transition-all">
                <div className="mb-3 inline-flex items-center justify-center w-12 h-12 bg-emerald-100 rounded-xl">
                  <DollarSign size={24} className="text-emerald-600" />
                </div>
                <h3 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent mb-2">
                  $647K
                </h3>
                <p className="text-slate-600 font-medium">{t('freeTrial.netAnnualSavings')}</p>
              </div>

              <div className="group p-6 bg-slate-50 border border-slate-200 rounded-2xl text-center hover:border-blue-300 hover:shadow-lg transition-all">
                <div className="mb-3 inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-xl">
                  <TrendingDown size={24} className="text-purple-600" />
                </div>
                <h3 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent mb-2">
                  30%
                </h3>
                <p className="text-slate-600 font-medium">{t('freeTrial.decreaseInITCosts')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('freeTrial.finalCtaTitle')}
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            {t('freeTrial.finalCtaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => openModal('trial')}
              className="group px-8 py-4 bg-white text-slate-900 rounded-xl font-semibold text-lg transition-all hover:bg-slate-100 flex items-center justify-center gap-3 transform hover:scale-[1.02] active:scale-95"
            >
              {t('freeTrial.startYourFreeTrial')}
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => openModal('demo')}
              className="px-8 py-4 bg-transparent hover:bg-white/10 border border-slate-500 rounded-xl font-medium text-lg transition-all text-white"
            >
              {t('freeTrial.requestADemo')}
            </button>
          </div>
        </div>
      </section>

      <DemoRequestModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        requestType={modalType}
      />
    </PageLayout>
  );
};

export default FreeTrial;
